/**
 * "We couldn't load your businesses" — the screen that used to be the signup
 * wizard.
 *
 * An empty `orgs` list has always meant one thing to every guard in Karr:
 * brand-new account, send them to `/onboarding`. It actually means two things,
 * and the second is a failure — an expired token, an RLS change, a network
 * blip, a `public.users` row that would not read. On that path a customer of
 * two years landed on "Tell us about your business" and was asked to create the
 * organisation they already have. Reported live 11 Aug 2026: *"even the clients
 * who have already signed up are again taken to this url and then they get
 * confused."*
 *
 * `useAuth().orgsUnreadable` now carries the reason, and this is what it draws
 * instead: what happened, what to do, and two buttons that land ON the fix —
 * read them again, or sign in again if the session is what expired.
 *
 * The reason splits into TWO different stories, and the panel tells the right
 * one (28 Sep 2026, clients hitting this "over and over"):
 *
 *   • a NETWORK failure — the fetch never got an HTTP answer. Karr is an
 *     installed PWA, so on a wobbly connection the cached shell paints
 *     perfectly and the very first API read is what fails. Sign-in advice is a
 *     lie here (signing out on a dead connection strands them at the sign-in
 *     screen), so the panel says "connection", retries itself on the browser's
 *     'online' event and on a gentle timer, and keeps the person's books one
 *     reconnect away.
 *   • everything else — a real API answer (RLS, an expired session, a broken
 *     read). Retrying on a timer would hammer a failure that never changes, so
 *     these keep the original copy and the two buttons.
 *
 * @since 2026-08-11
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { Navigate } from '@tanstack/react-router';
import { RefreshCw, Building2, WifiOff } from 'lucide-react';
import { Button } from '@/shared/components/Button';
import { useAuth } from '@/shared/hooks/useAuth';
import { reportEvent, reportError } from '@/shared/services/telemetry';
import { isDatabaseWaking, isNetworkFailure } from '@/shared/utils/edgeError';

/** Between self-retries while the cause is the network. One light read per go —
 *  frequent enough to feel instant when wifi returns a moment before the
 *  'online' event does (or without it: some webviews never fire one). */
const SELF_RETRY_EVERY_MS = 12_000;
/** While the database itself is restarting: it comes back in seconds, so ask often. */
const DB_WAKING_RETRY_EVERY_MS = 5_000;

export function OrgsUnavailable({ reason }: { reason: string }) {
    const { refreshProfile, signOut } = useAuth();
    const [retrying, setRetrying] = useState(false);
    const [leaving, setLeaving] = useState(false);
    const [goSignIn, setGoSignIn] = useState(false);
    const [offline, setOffline] = useState(() => typeof navigator !== 'undefined' && navigator.onLine === false);
    // One retry at a time: the timer, the 'online' event and the button can all
    // fire while a slow read is still in flight.
    const busy = useRef(false);

    const networkTrouble = isNetworkFailure(reason);
    const dbWaking = !networkTrouble && isDatabaseWaking(reason);

    // How often does anybody actually SEE this? Without the event we are back to
    // inferring the failure rate from whoever bothers to report it, which is how
    // the /onboarding bounce survived long enough to reach live clients.
    useEffect(() => {
        reportEvent('orgs_unavailable_shown', { reason });
        // …and reportEvent goes to PostHog/Sentry, which may not be configured —
        // on 28 Sep 2026 neither was, so clients saw this screen for weeks while
        // `npm run errors` showed nothing. This row lands in client_errors, where
        // the digest reads. (A network-dead case cannot reach the DB to log
        // itself; the ones that CAN log are exactly the ones somebody can fix.)
        reportError(new Error(`orgs unavailable: ${reason.slice(0, 300)}`), { kind: 'orgs_unavailable' });
    }, [reason]);

    const retry = useCallback(async () => {
        if (busy.current) return;
        busy.current = true;
        setRetrying(true);
        try {
            await refreshProfile();
        } finally {
            busy.current = false;
            setRetrying(false);
        }
    }, [refreshProfile]);

    // The retry that actually fixes the network case: the moment the browser
    // knows the connection is back, read again — nobody has to find a button.
    useEffect(() => {
        const onOnline = () => { setOffline(false); void retry(); };
        const onOffline = () => setOffline(true);
        window.addEventListener('online', onOnline);
        window.addEventListener('offline', onOffline);
        return () => {
            window.removeEventListener('online', onOnline);
            window.removeEventListener('offline', onOffline);
        };
    }, [retry]);

    // …and a gentle timer for the connections 'online' cannot see: a wifi that
    // is "up" but going nowhere, a captive portal that just got clicked through.
    // Network causes only — a 401 or an RLS refusal fails identically forever,
    // and a timer would just hammer it.
    // A database that is restarting fixes itself too, faster (schemawait).
    useEffect(() => {
        if (!networkTrouble && !dbWaking) return;
        const id = setInterval(() => {
            if (navigator.onLine !== false) void retry();
        }, dbWaking ? DB_WAKING_RETRY_EVERY_MS : SELF_RETRY_EVERY_MS);
        return () => clearInterval(id);
    }, [networkTrouble, dbWaking, retry]);

    if (goSignIn) return <Navigate to="/auth/signin" />;

    const startOver = async () => {
        setLeaving(true);
        await signOut();
        setGoSignIn(true);
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-stone-50 dark:bg-stone-900 px-4">
            <div className="w-full max-w-md rounded-lg border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-800 p-6 space-y-4">
                <div className="w-12 h-12 flex items-center justify-center">
                    {networkTrouble
                        ? <WifiOff className="w-6 h-6 text-amber-600 dark:text-amber-500" />
                        : <Building2 className="w-6 h-6 text-amber-600 dark:text-amber-500" />}
                </div>

                <div className="space-y-2">
                    <h1 className="text-lg font-semibold text-stone-900 dark:text-stone-100">
                        We couldn't load your businesses
                    </h1>
                    {networkTrouble ? (
                        <>
                            <p className="text-sm text-stone-600 dark:text-stone-400 leading-relaxed">
                                Your account is fine and nothing has been lost. Karr
                                could not reach its servers, which almost always
                                means this device's internet connection dropped
                                just as Karr opened.
                            </p>
                            <p className="text-sm text-stone-600 dark:text-stone-400 leading-relaxed">
                                {offline
                                    ? 'This device is offline right now. Keep this page open — the moment the connection comes back, Karr loads your books by itself.'
                                    : 'Keep this page open — Karr keeps retrying on its own. If it still fails on internet that otherwise works, switching between wifi and mobile data usually gets you through.'}
                            </p>
                        </>
                    ) : dbWaking ? (
                        <>
                            <p className="text-sm text-stone-600 dark:text-stone-400 leading-relaxed">
                                Your account is fine and nothing has been lost. Karr's
                                database was out of reach for a moment, so your businesses
                                could not be read yet.
                            </p>
                            <p className="text-sm text-stone-600 dark:text-stone-400 leading-relaxed">
                                Keep this page open. Karr asks again every few seconds and
                                opens your books the moment the database answers.
                            </p>
                        </>
                    ) : (
                        <>
                            <p className="text-sm text-stone-600 dark:text-stone-400 leading-relaxed">
                                Your account is fine and nothing has been lost. Karr just
                                could not read which businesses you belong to, so it can't
                                open your books yet.
                            </p>
                            <p className="text-sm text-stone-600 dark:text-stone-400 leading-relaxed">
                                Try again. If it keeps failing, your sign-in has most
                                likely expired — sign in once more and you'll land back on
                                your books.
                            </p>
                        </>
                    )}
                </div>

                <div className="grid grid-cols-2 gap-2">
                    <Button
                        variant="primary"
                        className="justify-center"
                        loading={retrying}
                        disabled={leaving}
                        icon={<RefreshCw size={15} />}
                        onClick={() => void retry()}
                    >
                        Try again
                    </Button>
                    <Button
                        variant="secondary"
                        className="justify-center"
                        loading={leaving}
                        disabled={retrying}
                        onClick={() => void startOver()}
                    >
                        Sign in again
                    </Button>
                </div>

                <p className="text-xs text-stone-400 dark:text-stone-500 border-t border-stone-100 dark:border-stone-700 pt-3">
                    Technical detail, for support: {reason}
                </p>
            </div>
        </div>
    );
}

export default OrgsUnavailable;
